import { Outlet, useLocation } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { useEffect } from 'react';
import { useMaterialUIController } from 'context';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import IdleContainer from 'components/Security/IdleContainer';
import ScrollTop from 'components/Scroll/ScrollTop';
import Icon from '@mui/material/Icon';
import LinearProgress from '@mui/material/LinearProgress';
import { useSelector } from 'react-redux';
import ErrorFallback from 'pages/template/PortalTemplate';
import PageLayout from 'components/LayoutContainers/PageLayout';
import PropTypes from 'prop-types';
import './standalone.css';
import FileViewerModal from 'pages/fileViewer/FileViewerModal';
import StandaloneNavbar from 'components/Navbars/StandaloneNavbar';
import { URL_STANDALONE_PATH } from 'utils/navigationUtils';

/**
 * The `StandaloneTemplate` component is responsible for rendering standalone routes in the application.
 * It wraps the routed content within an `IdleContainer` which manages session timeouts.
 * Inside the container, the routed content is presented using a `PageLayout`, and errors are caught and
 * handled using an `ErrorBoundary` that defaults to the `ErrorFallback` component.
 *
 * Additionally, this component also displays a 'scroll to top' button and a loading progress bar based on the application's state.
 *
 * @component
 * @prop {Object[]} routeConfigurations - An array of route configuration objects. Each object should have the path, name, and other related info.
 *
 * @example
 * <StandaloneTemplate routeConfigurations={myRouteConfigurations} />
 */
export function StandaloneTemplate({ routeConfigurations }) {
  const appState = useSelector((state) => state.app);
  const { setPageTitle } = useMaterialUIController();
  const {
    palette: { background }
  } = useTheme();

  const location = useLocation();
  useEffect(() => {
    setPageTitle(null);
  }, [location.key, setPageTitle]);

  const { getValue: conf } = useConf(useGetConfigurationQuery);
  const pathnameMap = buildPathnameMap(routeConfigurations);
  const currentRoute = findRoute(location.pathname, routeConfigurations);
  const currentRouteTemplate = currentRoute ? `${URL_STANDALONE_PATH}${currentRoute.path}` : null;
  return (
    <IdleContainer
      timeout={conf('security.session.timeout')}
      promptTimeout={conf('security.session.promptTimeout')}
    >
      <PageLayout
        title={pathnameMap.get(currentRouteTemplate)}
        display="flex"
        sx={{
          position: 'relative',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {currentRoute && !currentRoute.standalone?.navbarHidden && (
          <StandaloneNavbar view={currentRoute.view} />
        )}
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Outlet />
        </ErrorBoundary>
        <FileViewerModal />
      </PageLayout>

      <ScrollTop>
        <Icon fontSize="large" color="inherit">
          keyboard_double_arrow_up
        </Icon>
      </ScrollTop>

      {appState.appLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          pb={1}
          sx={{
            width: '100%',
            backgroundColor: background.default
          }}
        >
          <LinearProgress sx={{ borderRadius: 'unset' }} />
        </Box>
      )}
    </IdleContainer>
  );

  /**
   * buildPathnameMap map each route to its corresponding name.
   * The map will have the path as the key and the name as the value.
   *
   * @function
   * @param {Object[]} routeConfs - The list of route configurations.
   * @returns {Map<string, string>} - A map of path-to-name.
   */
  function buildPathnameMap(routeConfs) {
    return routeConfs.reduce((map, route) => {
      map.set(`${URL_STANDALONE_PATH}${route.path}`, route.name);
      return map;
    }, new Map());
  }

  /**
   * findRoute is a utility function that finds the route for a given path.
   * It uses regex to match dynamic parts of the route.
   *
   * @function
   * @param {string} path - The current path.
   * @param {Object[]} routesConf - The list of route configurations.
   * @returns {string|null} - The route if found, otherwise null.
   */
  function findRoute(path, routesConf) {
    let currentRoute = null;
    // Search with full prefix (/<viewType>/<viewName>)
    routesConf.some((route) => {
      const prefix = `${URL_STANDALONE_PATH}${route.viewType}/${route.view}`;
      if (path.startsWith(prefix)) {
        currentRoute = route;
        return true;
      }
      return false;
    });
    if (!currentRoute) {
      // Search with small prefix (/<viewType>)
      routesConf.some((route) => {
        const prefix = `${URL_STANDALONE_PATH}${route.viewType}`;
        if (path.startsWith(prefix)) {
          currentRoute = route;
          return true;
        }
        return false;
      });
    }

    return currentRoute;
  }
}

StandaloneTemplate.propTypes = {
  routeConfigurations: PropTypes.array.isRequired
};
